import React from 'react';
import { ArrowLeft, AlertCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { legalBrand } from './legalBrand';

const Disclaimer = ({ brand }) => {
  const b = legalBrand(brand);
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-6 py-8">
          <Link 
            to={b.home} 
            className="inline-flex items-center text-purple-600 hover:text-purple-700 mb-4 transition-colors"
          >
            <ArrowLeft size={20} className="mr-2" />
            {b.homeLabel}
          </Link>
          <h1 className="text-4xl font-light text-gray-900 mb-2">Disclaimer</h1>
          <p className="text-gray-600">Last Updated: August 22, 2026</p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="bg-white rounded-xl shadow-sm p-8 md:p-12 space-y-8">
          
          {/* Important Notice */}
          <div className="bg-yellow-50 border-l-4 border-yellow-600 p-6 rounded-r-lg">
            <div className="flex items-start">
              <AlertCircle className="text-yellow-600 mr-3 flex-shrink-0 mt-1" size={24} />
              <div>
                <h3 className="text-lg font-semibold text-yellow-900 mb-2">Important Notice</h3>
                <p className="text-yellow-800 leading-relaxed">
                  Please read this Disclaimer carefully before using this website. By accessing or using this website, 
                  you acknowledge that you have read, understood, and agree to be bound by this Disclaimer.
                </p>
              </div>
            </div>
          </div>

          {/* General Information */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">1. General Information Only</h2>
            <p className="text-gray-700 leading-relaxed">
              The information provided on this website is for general informational and educational purposes only. While we 
              strive to provide accurate and up-to-date information, we make no representations or warranties of any kind, 
              express or implied, about the completeness, accuracy, reliability, suitability, or availability of the information, 
              products, services, or related graphics contained on this website.
            </p>
          </section>

          {/* No Professional Advice */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">2. Not Professional Advice</h2>
            
            <div className="bg-gray-100 p-6 rounded-lg mb-4">
              <p className="text-gray-800 font-medium mb-3">
                The content on this website does NOT constitute professional advice and should not be relied upon as such.
              </p>
              <p className="text-gray-700 leading-relaxed">
                This includes, but is not limited to:
              </p>
            </div>

            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li><strong>Legal Advice:</strong> Nothing on this website should be construed as legal advice or create an attorney-client relationship</li>
              <li><strong>Financial Advice:</strong> No content should be interpreted as financial, investment, or tax advice</li>
              <li><strong>Medical Advice:</strong> Information provided is not medical advice and should not replace consultation with healthcare professionals</li>
              <li><strong>Professional Consultation:</strong> Content does not substitute for professional consultation in any field</li>
              <li><strong>Business Advice:</strong> Business information is general in nature and not tailored to specific circumstances</li>
              <li><strong>Technical Guidance:</strong> Technical information may not be suitable for all situations or implementations</li>
            </ul>

            <p className="text-gray-700 leading-relaxed mt-4">
              <strong>You should always consult with appropriate qualified professionals</strong> regarding any specific questions, 
              concerns, or circumstances relevant to your situation before making any decisions or taking any actions.
            </p>
          </section>

          {/* No Guarantees */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">3. No Guarantees or Warranties</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              We make no guarantees, warranties, or representations regarding:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>The accuracy, completeness, or timeliness of information provided</li>
              <li>Results you may obtain from using the information or services</li>
              <li>The suitability of content for your particular circumstances</li>
              <li>The uninterrupted or error-free operation of this website</li>
              <li>The correction of defects or errors</li>
              <li>The security of data transmitted through the website</li>
            </ul>
          </section>

          {/* Limitation of Liability */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">4. Limitation of Liability</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              TO THE FULLEST EXTENT PERMITTED BY LAW:
            </p>
            <div className="bg-red-50 border-2 border-red-200 rounded-lg p-6">
              <p className="text-gray-800 leading-relaxed mb-3">
                <strong>CT / KEEPER OF THE FIYAH</strong> and its affiliates, officers, directors, employees, agents, and 
                representatives SHALL NOT BE LIABLE for any direct, indirect, incidental, consequential, special, punitive, 
                or exemplary damages arising from:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li>Your use of or inability to use this website</li>
                <li>Reliance on any information provided on this website</li>
                <li>Errors, mistakes, or inaccuracies in content</li>
                <li>Personal injury or property damage resulting from your access to the website</li>
                <li>Unauthorized access to our servers or databases</li>
                <li>Interruption or cessation of transmission to or from the website</li>
                <li>Bugs, viruses, or other harmful components</li>
                <li>Loss of data, profits, or business opportunities</li>
                <li>Any other matter relating to this website</li>
              </ul>
            </div>
          </section>

          {/* Third-Party Content */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">5. Third-Party Links and Content</h2>
            <p className="text-gray-700 leading-relaxed">
              This website may contain links to third-party websites or resources. These links are provided for convenience only. 
              We have no control over the content, availability, or practices of third-party sites and accept no responsibility for:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4 mt-3">
              <li>The accuracy or reliability of third-party content</li>
              <li>Products or services offered by third parties</li>
              <li>Privacy practices of external websites</li>
              <li>Any damages or losses caused by third-party interactions</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-4">
              Inclusion of any link does not imply endorsement, sponsorship, or recommendation. You access third-party sites 
              at your own risk.
            </p>
          </section>

          {/* Personal Responsibility */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">6. Personal Responsibility</h2>
            <p className="text-gray-700 leading-relaxed">
              By using this website, you acknowledge and agree that:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4 mt-3">
              <li>You are solely responsible for your use of the website and any decisions you make based on its content</li>
              <li>You will conduct your own research and due diligence before taking any action</li>
              <li>You will seek professional advice when appropriate for your situation</li>
              <li>You assume all risks associated with your use of this website</li>
              <li>You will not hold us liable for any outcomes resulting from your use of the website</li>
            </ul>
          </section>

          {/* Changes to Content */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">7. Changes to Content</h2>
            <p className="text-gray-700 leading-relaxed">
              We reserve the right to modify, update, or remove any content on this website at any time without notice. 
              Information may become outdated, and we are under no obligation to update it. Historical content may not reflect 
              current circumstances, laws, regulations, or best practices.
            </p>
          </section>

          {/* No Endorsements */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">8. No Endorsements</h2>
            <p className="text-gray-700 leading-relaxed">
              Any mention of specific products, services, companies, organizations, or individuals does not constitute an 
              endorsement, recommendation, or guarantee of quality or performance. Opinions expressed are personal views and 
              may not represent factual statements.
            </p>
          </section>

          {/* Testimonials */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">9. Testimonials and Results</h2>
            <p className="text-gray-700 leading-relaxed">
              Any testimonials, case studies, or examples of results presented on this website represent individual experiences 
              and outcomes. They are not guarantees of similar results for you. Individual results may vary based on numerous 
              factors including effort, circumstances, background, and other variables.
            </p>
          </section>

          {/* Jurisdiction */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">10. Jurisdictional Issues</h2>
            <p className="text-gray-700 leading-relaxed">
              This website is controlled and operated from the Universe. We make no representation that materials on this website 
              are appropriate or available for use in other locations. If you access this website from other jurisdictions, you 
              do so at your own risk and are responsible for compliance with local laws.
            </p>
          </section>

          {/* Changes to Disclaimer */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">11. Changes to This Disclaimer</h2>
            <p className="text-gray-700 leading-relaxed">
              We reserve the right to modify this Disclaimer at any time. Changes will be posted on this page with an updated 
              "Last Updated" date. Your continued use of the website after any changes constitutes acceptance of the revised Disclaimer.
            </p>
          </section>

          {/* Contact */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">12. Questions About This Disclaimer</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              If you have any questions about this Disclaimer, please contact us at:
            </p>
            <div className="mt-4 p-6 bg-gray-50 rounded-lg">
              <p className="text-gray-800 font-medium">CT / KEEPER OF THE FIYAH</p>
              <p className="text-gray-700">Email: {b.privacy}</p>
            </div>
          </section>

          {/* Acceptance */}
          <section className="border-t border-gray-200 pt-8">
            <div className="bg-purple-50 border-2 border-purple-200 rounded-lg p-6">
              <p className="text-gray-800 font-medium mb-3">By using this website, you acknowledge that:</p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li>You have read and understood this Disclaimer</li>
                <li>You agree to be bound by its terms</li>
                <li>You accept all risks associated with your use of the website</li>
                <li>You will not hold us liable for any outcomes resulting from your use</li>
              </ul>
              <p className="text-gray-700 mt-4 italic">
                If you do not agree with this Disclaimer, please do not use this website.
              </p>
            </div>
          </section>

        </div>
      </div>
    </div>
  );
};

export default Disclaimer;